import React, {
  Fragment,
  Reducer,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react'
import { Button, LabeledInput, Select } from '@apex/component-library'
import {
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  Icon,
  Text,
} from '@chakra-ui/react'
import { FaTrashAlt } from 'react-icons/fa'
import { v4 as uuid } from 'uuid'
import {
  TransferInitiationAssetType,
  TransferInitiationAssetWebDto,
} from '../../../api/types'
import { OtherSecuritiesErrors, PositionEnum } from './types'
import { ALPHANUMERIC } from '../../../utils/regexConstants'

interface OtherSecuritiesFormProps {
  errors?: OtherSecuritiesErrors[]
  assets?: TransferInitiationAssetWebDto[]
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

enum FieldsEnum {
  IsShort = 'isShort',
  Identifier = 'identifier',
  Amount = 'amount',
  AssetType = 'assetType',
}

type AssetAction = {
  type: 'ADD' | 'REMOVE' | 'UPDATE'
  rowId?: string
  value?: string | boolean | number
  field?: FieldsEnum
}

const reducer: Reducer<TransferInitiationAssetWebDto[], AssetAction> = (
  state: TransferInitiationAssetWebDto[],
  action: AssetAction
) => {
  switch (action.type) {
    case 'ADD':
      return [
        ...state,
        {
          rowId: uuid(),
          isShort: undefined,
          identifier: undefined,
          amount: undefined,
          assetType: TransferInitiationAssetType.Symbol,
        },
      ]
    case 'REMOVE':
      return state.filter(item => item.rowId !== action.rowId)
    case 'UPDATE':
      return state.map(item => {
        if (item.rowId === action.rowId && action.field) {
          return { ...item, [action.field]: action.value }
        }
        return item
      })
    default:
      return state
  }
}

interface FieldTouched {
  [index: string]: boolean
}

const OtherSecuritiesForm = ({
  errors,
  assets,
  setFieldValue,
}: OtherSecuritiesFormProps) => {
  const [assetsList, dispatch] = useReducer(reducer, assets || [])
  const [fieldTouched, setFieldTouched] = useState<FieldTouched>({})

  const setFieldValueMemo = useCallback(setFieldValue, [])

  useEffect(() => {
    setFieldValueMemo('assets', assetsList)
  }, [assetsList, setFieldValueMemo])

  const errorsMap: {
    [index: string]: {
      isShort?: { content: string }
      identifier?: { content: string }
      amount?: { content: string }
    }
  } = {}
  ;(errors || []).forEach(
    (
      value: { isShort?: string; identifier?: string; amount?: string },
      index: number
    ) => {
      if (value) {
        errorsMap[index] = {
          ...(value.isShort && { isShort: { content: value.isShort } }),
          ...(value.identifier && {
            identifier: { content: value.identifier },
          }),
          ...(value.amount && { amount: { content: value.amount } }),
        }
      }
    }
  )

  const getError = (
    index: number,
    rowId: string,
    fieldName: 'isShort' | 'identifier' | 'amount'
  ) => {
    const rowErrors = errorsMap[index]
    const fieldError =
      rowErrors && fieldTouched[`${fieldName}${rowId}`] && rowErrors[fieldName]
    return fieldError ? fieldError : undefined
  }

  const validateIdentifier = (rowIndex: number, id: string) => {
    return getError(rowIndex, id, 'identifier')?.content
  }

  const handleBlur = (rowId: string, fieldName: string) =>
    setFieldTouched(previous => ({
      ...previous,
      [`${fieldName}${rowId}`]: true,
    }))

  const titleSpan = 4
  const positionSpan = 2
  const identifierSpan = 4
  const amountSpan = 2
  const assetTypeSpan = 3
  const trashSpan = 6 // Should bring totalColumnSpan up to 21 for form consistency
  const totalColumnSpan =
    titleSpan +
    positionSpan +
    identifierSpan +
    amountSpan +
    assetTypeSpan +
    trashSpan

  return (
    <Grid
      borderBottom="1px solid gray"
      gridGap={4}
      padding="1rem 0 1rem 1rem"
      templateRows={`repeat(${assets?.length}, fit-content 1fr)`}
      templateColumns={`repeat(${totalColumnSpan}, 1fr)`}
    >
      <GridItem colSpan={titleSpan} rowSpan={assets?.length! + 1}>
        <Text fontWeight="semibold">Other Securities</Text>
        <Button
          data-testid="add-security"
          mt={4}
          variant="link"
          onClick={() => dispatch({ type: 'ADD' })}
        >
          + Add Security
        </Button>
      </GridItem>
      <GridItem colSpan={positionSpan} hidden={!assets?.length}>
        <Text fontWeight="semibold">Position*</Text>
      </GridItem>
      <GridItem colSpan={identifierSpan} hidden={!assets?.length}>
        <Text fontWeight="semibold">Identifier*</Text>
      </GridItem>
      <GridItem colSpan={amountSpan} hidden={!assets?.length} justifySelf="end">
        <Text fontWeight="semibold">Quantity*</Text>
      </GridItem>
      <GridItem colSpan={assetTypeSpan + trashSpan} hidden={!assets?.length}>
        <Text fontWeight="semibold">Type*</Text>
      </GridItem>
      {assets?.map(
        (
          {
            rowId,
            isShort,
            identifier,
            amount,
            assetType,
          }: TransferInitiationAssetWebDto,
          index: number
        ) => {
          const isShortErrorMessage = getError(index, rowId!, 'isShort')
            ?.content
          const identifierErrorMessage = validateIdentifier(index, rowId!)
          const amountErrorMessage = getError(index, rowId!, 'amount')?.content

          let isShortToString: string = ''
          if (isShort === undefined) {
            isShortToString = ''
          } else if (isShort) {
            isShortToString = 'Short'
          } else if (!isShort) {
            isShortToString = 'Long'
          }

          return (
            <Fragment key={rowId}>
              <GridItem colSpan={positionSpan}>
                <FormControl isInvalid={!!isShortErrorMessage}>
                  <Select
                    data-testid="assets-isShort-select"
                    isInvalid={!!isShortErrorMessage}
                    value={isShortToString}
                    onBlur={() => handleBlur(rowId!, 'isShort')}
                    onChange={e =>
                      dispatch({
                        type: 'UPDATE',
                        value: e.target.value
                          ? e.target.value === 'Short'
                          : undefined,
                        rowId,
                        field: FieldsEnum.IsShort,
                      })
                    }
                  >
                    <option />
                    <option value={PositionEnum.Short}>
                      {PositionEnum.Short}
                    </option>
                    <option value={PositionEnum.Long}>
                      {PositionEnum.Long}
                    </option>
                  </Select>
                  <FormErrorMessage>{isShortErrorMessage}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={identifierSpan}>
                <FormControl isInvalid={!!identifierErrorMessage}>
                  <LabeledInput
                    data-testid="assets-identifier-input"
                    value={identifier || ''}
                    isInvalid={!!identifierErrorMessage}
                    label=""
                    type="text"
                    onBlur={() => handleBlur(rowId!, 'identifier')}
                    onChange={e =>
                      dispatch({
                        type: 'UPDATE',
                        value: e.target.value || undefined,
                        rowId,
                        field: FieldsEnum.Identifier,
                      })
                    }
                  />
                  <FormErrorMessage>{identifierErrorMessage}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={amountSpan}>
                <FormControl isInvalid={!!amountErrorMessage}>
                  <LabeledInput
                    data-testid="assets-amount-input"
                    textAlign="right"
                    value={amount || ''}
                    isInvalid={!!amountErrorMessage}
                    label=""
                    type="number"
                    onBlur={() => handleBlur(rowId!, 'amount')}
                    onChange={e =>
                      dispatch({
                        type: 'UPDATE',
                        value:
                          e.target.value && !isNaN(+e.target.value)
                            ? +e.target.value
                            : undefined,
                        rowId,
                        field: FieldsEnum.Amount,
                      })
                    }
                  />
                  <FormErrorMessage>{amountErrorMessage}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={assetTypeSpan}>
                <Select
                  value={assetType}
                  onBlur={() => handleBlur(rowId!, 'assetType')}
                  onChange={e =>
                    dispatch({
                      type: 'UPDATE',
                      value: e.target.value,
                      rowId,
                      field: FieldsEnum.AssetType,
                    })
                  }
                >
                  <option value={TransferInitiationAssetType.Cusip}>
                    {TransferInitiationAssetType.Cusip}
                  </option>
                  <option value={TransferInitiationAssetType.Isin}>
                    {TransferInitiationAssetType.Isin}
                  </option>
                  <option value={TransferInitiationAssetType.Sedol}>
                    {TransferInitiationAssetType.Sedol}
                  </option>
                  <option value={TransferInitiationAssetType.Symbol}>
                    Symbol
                  </option>
                </Select>
              </GridItem>
              <GridItem colSpan={trashSpan} justifySelf="end">
                <Button
                  onClick={() => dispatch({ type: 'REMOVE', rowId })}
                  variant="link"
                >
                  <Icon
                    data-testid="assets-delete-button"
                    as={FaTrashAlt}
                    color="gray.400"
                    h={6}
                    w={6}
                  />
                </Button>
              </GridItem>
            </Fragment>
          )
        }
      )}
    </Grid>
  )
}

export default OtherSecuritiesForm
